/**
 * SourceGenerator Form Component
 * Form for creating/editing source generators
 *
 * @version 2025.1.0
 */
import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import {
  SourceGenerator,
  SourceGeneratorFormData,
  SourceGeneratorRiskType,
  SourceGeneratorStatus,
  RISK_TYPE_LABELS,
  STATUS_LABELS,
} from '../../../types/sourceGenerator';

interface SourceGeneratorFormProps {
  initialData?: SourceGenerator;
  onSubmit: (data: SourceGeneratorFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const defaultFormData: SourceGeneratorFormData = {
  name: '',
  location: '',
  riskType: SourceGeneratorRiskType.PHYSICAL,
  description: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  yearOfManufacture: null,
  maintenanceFrequency: '',
  lastMaintenanceDate: null,
  nextMaintenanceDate: null,
  observations: '',
  status: SourceGeneratorStatus.ACTIVE,
};

/**
 * Converts a SourceGenerator to SourceGeneratorFormData
 */
function getFormDataFromSourceGenerator(data: SourceGenerator): SourceGeneratorFormData {
  return {
    name: data.name,
    location: data.location,
    riskType: data.riskType,
    description: data.description,
    manufacturer: data.manufacturer ?? '',
    model: data.model ?? '',
    serialNumber: data.serialNumber ?? '',
    yearOfManufacture: data.yearOfManufacture ?? null,
    maintenanceFrequency: data.maintenanceFrequency ?? '',
    lastMaintenanceDate: data.lastMaintenanceDate?.split('T')[0] || null,
    nextMaintenanceDate: data.nextMaintenanceDate?.split('T')[0] || null,
    observations: data.observations ?? '',
    status: data.status,
  };
}

export const SourceGeneratorForm: React.FC<SourceGeneratorFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<SourceGeneratorFormData>(
    () => initialData ? getFormDataFromSourceGenerator(initialData) : defaultFormData
  );
  const [errors, setErrors] = useState<Partial<Record<keyof SourceGeneratorFormData, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (field: keyof SourceGeneratorFormData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error on change
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof SourceGeneratorFormData, string>> = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Nome deve ter no mínimo 2 caracteres';
    }

    if (!formData.location || formData.location.trim().length === 0) {
      newErrors.location = 'Localização é obrigatória';
    }

    if (!formData.riskType) {
      newErrors.riskType = 'Tipo de risco é obrigatório';
    }

    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Descrição deve ter no mínimo 10 caracteres';
    }

    const currentYear = new Date().getFullYear();
    if (formData.yearOfManufacture) {
      if (formData.yearOfManufacture < 1900 || formData.yearOfManufacture > currentYear + 1) {
        newErrors.yearOfManufacture = 'Ano de fabricação inválido';
      }
    }

    if (formData.lastMaintenanceDate && formData.nextMaintenanceDate) {
      const lastDate = new Date(formData.lastMaintenanceDate);
      const nextDate = new Date(formData.nextMaintenanceDate);
      if (nextDate <= lastDate) {
        newErrors.nextMaintenanceDate = 'Data da próxima manutenção deve ser posterior à última';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err: unknown) {
      const error = err as Error;
      setSubmitError(error.message ?? 'Erro ao salvar fonte geradora');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}
      {/* Basic Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Informações Básicas
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                required
                label="Nome"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                required
                label="Localização"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                error={!!errors.location}
                helperText={errors.location}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth required error={!!errors.riskType}>
                <InputLabel>Tipo de Risco</InputLabel>
                <Select
                  value={formData.riskType}
                  label="Tipo de Risco"
                  onChange={(e) => handleChange('riskType', e.target.value)}
                  disabled={loading}
                >
                  {Object.values(SourceGeneratorRiskType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {RISK_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                  disabled={loading}
                >
                  {Object.values(SourceGeneratorStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Descrição"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                disabled={loading}
              />
            </Grid>
          </Grid>
      </Box>
      {/* Equipment Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Informações do Equipamento
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Fabricante"
                value={formData.manufacturer ?? ''}
                onChange={(e) => handleChange('manufacturer', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Modelo"
                value={formData.model ?? ''}
                onChange={(e) => handleChange('model', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Número de Série"
                value={formData.serialNumber ?? ''}
                onChange={(e) => handleChange('serialNumber', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                type="number"
                label="Ano de Fabricação"
                value={formData.yearOfManufacture ?? ''}
                onChange={(e) => handleChange('yearOfManufacture', e.target.value ? parseInt(e.target.value) : null)}
                error={!!errors.yearOfManufacture}
                helperText={errors.yearOfManufacture}
                disabled={loading}
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
              />
            </Grid>
          </Grid>
      </Box>
      {/* Maintenance Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Manutenção
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Frequência de Manutenção"
                placeholder="Ex: Mensal, Trimestral, Anual"
                value={formData.maintenanceFrequency ?? ''}
                onChange={(e) => handleChange('maintenanceFrequency', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                type="date"
                label="Última Manutenção"
                value={formData.lastMaintenanceDate ?? ''}
                onChange={(e) => handleChange('lastMaintenanceDate', e.target.value ?? null)}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                type="date"
                label="Próxima Manutenção"
                value={formData.nextMaintenanceDate ?? ''}
                onChange={(e) => handleChange('nextMaintenanceDate', e.target.value ?? null)}
                error={!!errors.nextMaintenanceDate}
                helperText={errors.nextMaintenanceDate}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
      </Box>
      {/* Observations */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Observações
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            placeholder="Observações adicionais sobre a fonte geradora..."
            value={formData.observations ?? ''}
            onChange={(e) => handleChange('observations', e.target.value)}
            disabled={loading}
          />
      </Box>
      <Divider sx={{ my: 3 }} />
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </Box>
    </Box>
  );
};

export default SourceGeneratorForm;
